'use client';

import React, { useState, useEffect, useRef } from 'react';
import Icon from '@/components/ui/AppIcon';
import { useLanguage } from '@/contexts/LanguageContext';

interface HeroSectionProps {
  onApplyClick: () => void;
  onWatchStoriesClick: () => void;
}

const HeroSection = ({ onApplyClick, onWatchStoriesClick }: HeroSectionProps) => {
  const { t } = useLanguage();
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 100);
    return () => clearTimeout(timer);
  }, []);

  return (
    <section ref={sectionRef} className="relative min-h-screen flex items-center overflow-hidden bg-white pt-20">
      {/* Subtle background elements */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-blue-50 rounded-full blur-3xl opacity-60 translate-x-1/3 -translate-y-1/4" />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-blue-50 rounded-full blur-3xl opacity-40 -translate-x-1/4 translate-y-1/4" />
        <div className="absolute top-1/2 left-1/2 w-[300px] h-[300px] -translate-x-1/2 -translate-y-1/2 opacity-20">
          <div className="w-full h-full rounded-full border border-[#1D6FE8]/20 animate-ping" style={{ animationDuration: '4s' }} />
        </div>
      </div>

      <div className="relative z-10 w-full max-w-7xl mx-auto px-6 lg:px-8 py-20">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Left: Text Content */}
          <div className={`transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            {/* Eyebrow */}
            <div className="inline-flex items-center gap-2 mb-8">
              <div className="w-1.5 h-1.5 rounded-full bg-[#1D6FE8]" />
              <span className="text-sm font-body font-medium text-[#1D6FE8] tracking-wide uppercase">
                {t('hero.eyebrow')}
              </span>
            </div>

            {/* Headline */}
            <h1 className="font-display font-bold text-[#0A1628] leading-[1.05] tracking-tight mb-6"
              style={{ fontSize: 'clamp(2.5rem, 5vw, 4.5rem)' }}>
              {t('hero.headline')}{' '}
              <br className="hidden sm:block" />
              <span className="gradient-text-blue">{t('hero.headlineBrand')}</span>
            </h1>

            {/* Subtitle */}
            <p className="font-body text-xl text-[#4A5568] leading-relaxed mb-10 max-w-lg">
              {t('hero.subtitle')}
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 mb-12">
              <button
                onClick={onApplyClick}
                className="inline-flex items-center justify-center gap-2 bg-[#0A1628] text-white font-display font-semibold text-base px-8 py-4 rounded-2xl hover:bg-[#1D6FE8] transition-all duration-350 shadow-cta hover:shadow-blue hover:-translate-y-0.5"
              >
                {t('cta.createProfile')}
                <Icon name="ArrowRightIcon" size={18} />
              </button>
              <button
                onClick={onWatchStoriesClick}
                className="inline-flex items-center justify-center gap-2 bg-white text-[#0A1628] font-display font-semibold text-base px-8 py-4 rounded-2xl border border-[#E8EDF5] hover:border-[#1D6FE8]/30 hover:bg-[#EEF4FD] transition-all duration-350"
              >
                {t('cta.learnMore')}
              </button>
            </div>

            {/* Trust indicators */}
            <div className="flex items-center gap-6 flex-wrap">
              <div className="flex items-center gap-2">
                <div className="flex -space-x-2">
                  {['bg-[#1D6FE8]', 'bg-[#C9A84C]', 'bg-[#0A1628]'].map((bg, i) => (
                    <div key={i} className={`w-8 h-8 rounded-full ${bg} border-2 border-white flex items-center justify-center`}>
                      <Icon name="UserIcon" size={14} className="text-white" />
                    </div>
                  ))}
                </div>
                <span className="text-sm font-body text-[#4A5568]">
                  <span className="font-semibold text-[#0A1628]">847+</span> {t('hero.activeAmbassadors')}
                </span>
              </div>
              <div className="w-px h-4 bg-[#E8EDF5]" />
              <div className="flex items-center gap-1.5">
                {[1,2,3,4,5].map(i => (
                  <Icon key={i} name="StarIcon" size={14} className="text-[#C9A84C]" />
                ))}
                <span className="text-sm font-body text-[#4A5568] ml-1">12,500+ {t('hero.transformations')}</span>
              </div>
            </div>
          </div>

          {/* Right: Ambassador Profile Mockup */}
          <div className={`transition-all duration-700 delay-200 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <div className="relative">
              {/* Floating card glow */}
              <div className="absolute inset-0 bg-gradient-to-br from-[#1D6FE8]/10 to-[#C9A84C]/10 rounded-3xl blur-2xl scale-105" />

              {/* Profile Card */}
              <div className="relative bg-white rounded-3xl shadow-glass border border-[#E8EDF5] overflow-hidden animate-float">
                {/* Card Header / Cover */}
                <div className="h-28 bg-gradient-to-br from-[#0A1628] to-[#1D6FE8] relative">
                  <div className="absolute inset-0 opacity-20">
                    <div className="absolute top-2 right-4 w-20 h-20 rounded-full bg-white/10 blur-xl" />
                    <div className="absolute bottom-0 left-8 w-16 h-16 rounded-full bg-[#C9A84C]/30 blur-lg" />
                  </div>
                  {/* Verified badge */}
                  <div className="absolute top-4 right-4 flex items-center gap-1.5 bg-white/15 backdrop-blur-sm rounded-full px-3 py-1">
                    <div className="w-1.5 h-1.5 rounded-full bg-[#C9A84C]" />
                    <span className="text-white text-xs font-body font-medium">Verified Ambassador</span>
                  </div>
                </div>

                <div className="px-6 pb-6">
                  {/* Avatar */}
                  <div className="flex items-end justify-between -mt-10 mb-4">
                    <div className="relative">
                      <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-[#1D6FE8] to-[#0A1628] border-4 border-white shadow-card flex items-center justify-center">
                        <Icon name="UserIcon" size={36} className="text-white" />
                      </div>
                      <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-[#16A34A] rounded-full border-2 border-white" />
                    </div>
                    <div className="flex gap-2 mt-2">
                      <div className="w-8 h-8 rounded-xl bg-[#F4F7FC] flex items-center justify-center hover:bg-[#EEF4FD] transition-colors cursor-pointer">
                        <Icon name="GlobeAltIcon" size={16} className="text-[#1D6FE8]" />
                      </div>
                      <div className="w-8 h-8 rounded-xl bg-[#F4F7FC] flex items-center justify-center hover:bg-[#EEF4FD] transition-colors cursor-pointer">
                        <Icon name="ShareIcon" size={16} className="text-[#1D6FE8]" />
                      </div>
                    </div>
                  </div>

                  {/* Name & Title */}
                  <div className="mb-3">
                    <h3 className="font-display font-bold text-xl text-[#0A1628]">{t('hero.profileName')}</h3>
                    <p className="text-sm font-body text-[#1D6FE8] font-medium">{t('hero.profileTitle')}</p>
                  </div>

                  {/* Bio */}
                  <p className="text-sm font-body text-[#4A5568] leading-relaxed mb-4">
                    {t('hero.profileBio')}
                  </p>

                  {/* Services */}
                  <div className="flex flex-wrap gap-2 mb-5">
                    {['Neuroakashic™', 'Quantum Scans', 'Coaching'].map((tag) => (
                      <span key={tag} className="text-xs font-body font-medium bg-[#EEF4FD] text-[#1D6FE8] px-3 py-1 rounded-full">
                        {tag}
                      </span>
                    ))}
                  </div>

                  {/* URL */}
                  <div className="flex items-center gap-2 bg-[#F4F7FC] rounded-xl px-3 py-2 mb-4">
                    <Icon name="LinkIcon" size={14} className="text-[#C9A84C] flex-shrink-0" />
                    <span className="text-xs font-mono text-[#4A5568] truncate">{t('hero.profileUrl')}</span>
                  </div>

                  {/* CTA Buttons */}
                  <div className="grid grid-cols-2 gap-3">
                    <button className="flex items-center justify-center gap-1.5 bg-[#F4F7FC] text-[#0A1628] text-sm font-display font-semibold py-2.5 rounded-xl hover:bg-[#EEF4FD] transition-colors">
                      <Icon name="EnvelopeIcon" size={15} />
                      {t('hero.profileContact')}
                    </button>
                    <button className="flex items-center justify-center gap-1.5 bg-[#0A1628] text-white text-sm font-display font-semibold py-2.5 rounded-xl hover:bg-[#1D6FE8] transition-colors">
                      <Icon name="UserPlusIcon" size={15} />
                      {t('hero.profileRegister')}
                    </button>
                  </div>
                </div>
              </div>

              {/* Floating stat badges */}
              <div className="absolute -left-6 top-1/3 bg-white rounded-2xl shadow-card border border-[#E8EDF5] px-4 py-3 animate-float" style={{ animationDelay: '1s' }}>
                <div className="text-lg font-display font-bold text-[#0A1628]">$5,420</div>
                <div className="text-xs font-body text-[#4A5568]">{t('hero.averageIncome')}</div>
              </div>

              <div className="absolute -right-4 bottom-1/4 bg-white rounded-2xl shadow-card border border-[#E8EDF5] px-4 py-3 animate-float" style={{ animationDelay: '2s' }}>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-[#16A34A] animate-pulse" />
                  <span className="text-xs font-body font-medium text-[#0A1628]">847 Active</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-[#4A5568]/60">
        <div className="w-px h-8 bg-gradient-to-b from-transparent to-[#4A5568]/30" />
        <Icon name="ChevronDownIcon" size={20} className="animate-bounce" />
      </div>
    </section>
  );
};

export default HeroSection;